import {areSetsEqual} from '@benchling/js-utils';

import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {addStepWithInferredEdges} from 'pipelines/network/addStepWithInferredEdges';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {CreateStepParams} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import APIUtil from 'util/APIUtil';
import {checkClipboardReadAccess} from 'util/clipboard/ClipboardUtil';

import {COPY_STEPS_CLIPBOARD_CONTENT_TYPE, CopyStepsClipboardContent} from './copyStepsToClipboard';

const COULD_NOT_READ_CLIPBOARD_MSG = 'Unable to read from clipboard.';
const INVALID_CLIPBOARD_CONTENT_MSG = 'Could not paste steps, because the clipboard does not contain steps.';

const SCRATCH_PLATE_IDS_CONFIG_KEY = 'scratchPlateIds';

// Returns the IDs only if every comma-separated value is a scratch plate API ID (e.g. "scrplt_1,scrplt_2").
function parseScratchPlateIds(text: string): Array<string> | null {
  if (!BenchlingConfig.PIPELINES_ENABLE_EXPERIMENTAL_SCRATCH_PLATE_STEPS) {
    return null;
  }
  const scratchPlateIds = text
    .split(',')
    .map((scratchPlateId) => scratchPlateId.trim())
    .filter((scratchPlateId) => scratchPlateId.length > 0);
  if (
    scratchPlateIds.length === 0 ||
    !scratchPlateIds.every((scratchPlateId) => APIUtil.isScratchPlate(scratchPlateId))
  ) {
    return null;
  }
  return scratchPlateIds;
}

function isCopyStepsClipboardContent(value: unknown): value is CopyStepsClipboardContent {
  if (
    value == null ||
    typeof value !== 'object' ||
    !areSetsEqual(new Set(Object.keys(value)), new Set(['type', 'steps']))
  ) {
    return false;
  }
  const {type, steps} = value as CopyStepsClipboardContent;
  return (
    type === COPY_STEPS_CLIPBOARD_CONTENT_TYPE &&
    steps.every(
      (step: unknown) => typeof step === 'object' && step != null && step.hasOwnProperty('stepType')
    )
  );
}

async function readClipboardText(): Promise<string | null> {
  // `checkClipboardReadAccess` shows its own notification if access is denied.
  if (!(await checkClipboardReadAccess())) {
    return null;
  }
  try {
    return await navigator.clipboard.readText();
  } catch (e) {
    // `readText` can still throw a `DOMException` even after `checkClipboardReadAccess` passed.
    if (e instanceof DOMException) {
      notifications.error(COULD_NOT_READ_CLIPBOARD_MSG);
      return null;
    }
    throw e;
  }
}

function parseStepsFromClipboardText(text: string): Array<CreateStepParams> | null {
  try {
    const json = JSON.parse(text);
    if (isCopyStepsClipboardContent(json) && json.steps.length > 0) {
      return json.steps;
    }
  } catch (e) {
    // `SyntaxError` is raised if the clipboard content is not JSON; all other exceptions are unexpected.
    if (!(e instanceof SyntaxError)) {
      throw e;
    }
  }
  return null;
}

// Create the pasted steps and return their IDs. Notifies the user and returns an empty array if the clipboard
// content is neither valid copied steps nor scratch plate IDs.
async function createPastedSteps(
  pipeline: Pick<PipelineGraphQL, 'steps' | 'id'>,
  text: string
): Promise<Array<string>> {
  const scratchPlateIds = parseScratchPlateIds(text);
  if (scratchPlateIds != null) {
    const {stepId} = await addStepWithInferredEdges(pipeline, {
      stepType: PipelineStepType.IMPORT_PLATES,
      config: {[SCRATCH_PLATE_IDS_CONFIG_KEY]: scratchPlateIds},
      runAfterCreate: true,
    });
    return [stepId];
  }

  const steps = parseStepsFromClipboardText(text);
  if (steps == null || steps.length === 0) {
    notifications.error(INVALID_CLIPBOARD_CONTENT_MSG);
    return [];
  }

  const {newStepIds} = await PipelineDAO.addAndUpdatePipelineSteps(pipeline.id, {
    stepsToAdd: steps,
    stepsToUpdate: [],
  });
  notifications.success(`Pasted ${newStepIds.length} ${pluralize(newStepIds.length, 'step')} from clipboard`);
  return newStepIds;
}

export interface PasteFromClipboardResult {
  // Set when the paste creates exactly one step, so the caller can open its config editor.
  createdStepId?: string;
}

export async function pasteStepsFromClipboard(
  pipeline: Pick<PipelineGraphQL, 'steps' | 'id'>
): Promise<PasteFromClipboardResult> {
  const text = await readClipboardText();
  if (text == null) {
    return {};
  }

  const createdStepIds = await createPastedSteps(pipeline, text);
  // When the paste creates a single step, surface its ID so the caller can open its config editor.
  return createdStepIds.length === 1 ? {createdStepId: createdStepIds[0]} : {};
}
